import get from 'lodash/get';
import memoize from 'memoize-one';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import React from 'react';

import Loading from '../../generic/Loading';
import { isTaskOngoing } from '../../generic/TaskStatus';
import Titled from '../../layout/Titled';
import {
  bookingEntryShape,
  campaignShape,
  detailedInvoiceShape,
  opportunityShape,
  productShape,
  taskShape,
} from '../../prop-types';
import AddBookingEntryModal from '../booking-entries/AddBookingEntry';
import GenerateBookingEntriesButton from '../booking-entries/GenerateBookingEntriesButton';
import GenerateCoInvestBookingEntriesButton from '../booking-entries/GenerateCoInvestBookingEntriesButton';
import GenerateNonMediaBookingEntriesButton from '../booking-entries/GenerateNonMediaBookingEntriesButton';
import ListBookingEntries from '../booking-entries/ListBookingEntries';
import CreateInvoiceButton from '../invoices/CreateInvoiceButton';
import ListInvoices from '../invoices/ListInvoices';
import { aggregateCampaignBudgets } from '../utils';
import Sums from './Sums';

class InvoiceModalContent extends React.Component {
  state = {
    openInvoiceId: null,
  };

  componentDidMount() {
    const { initialized, onInitialize, opportunity } = this.props;

    if (!initialized) {
      onInitialize({
        opportunityId: opportunity.id,
        latestTask: opportunity.latestTask,
      });
    }
  }

  handleCreateInvoiceSuccess = ({ value: inv }) => {
    this.setState({ openInvoiceId: inv.id });
  };

  /* eslint-disable react/sort-comp */
  aggregateCampaignBudgets = memoize(aggregateCampaignBudgets);

  render() {
    const {
      bookingEntries,
      invoices,
      opportunity,
      onDeleteBookingEntry,
      onDeleteInvoice,
      loading,
      latestTask,
      isEditing,
      staticData: { products, campaign },
    } = this.props;

    const openInvoiceId = this.state.openInvoiceId || this.props.openInvoiceId;
    const { id: opportunityId } = opportunity;

    const budgets = this.aggregateCampaignBudgets(campaign);

    return loading ? (
      <Loading />
    ) : (
      <div data-qa-field="balance-modal-content">
        <Titled
          title="Open Entries"
          size="xs"
          actions={[
            <GenerateBookingEntriesButton
              opportunityId={opportunityId}
              disabled={isTaskOngoing(get(latestTask, 'status')) || !isEditing}
            />,
            <GenerateNonMediaBookingEntriesButton
              campaign={campaign}
              products={products}
              disabled={!isEditing}
            />,
            <GenerateCoInvestBookingEntriesButton
              campaign={campaign}
              products={products}
              disabled={!isEditing}
            />,
            <AddBookingEntryModal
              opportunityId={opportunityId}
              products={products}
              disabled={!isEditing}
            />,
          ]}
        >
          <ListBookingEntries
            onDelete={onDeleteBookingEntry}
            data={bookingEntries}
            products={products}
            showUpdateButton
            showHeader
            allowBulkEdit
            budgets={budgets}
            isEditing={isEditing}
          />
        </Titled>
        <Titled
          title="Invoices"
          size="xs"
          className="mt-5"
          actions={[
            <CreateInvoiceButton
              opportunity={opportunity}
              onSuccess={this.handleCreateInvoiceSuccess}
              disabled={!isEditing}
            />,
          ]}
        >
          <ListInvoices
            invoices={invoices}
            products={products}
            onDelete={onDeleteInvoice}
            key={openInvoiceId}
            openInvoiceId={openInvoiceId}
            isEditing={isEditing}
          />
        </Titled>
        <Sums opportunity={opportunity} netClientBudget={budgets.total} />
      </div>
    );
  }
}

InvoiceModalContent.propTypes = {
  opportunity: opportunityShape.isRequired,
  bookingEntries: arrayOf(bookingEntryShape.isRequired).isRequired,
  invoices: arrayOf(detailedInvoiceShape.isRequired).isRequired,
  initialized: bool.isRequired,
  onInitialize: func.isRequired,
  onDeleteBookingEntry: func.isRequired,
  onDeleteInvoice: func.isRequired,
  loading: bool.isRequired,
  staticData: shape({
    products: arrayOf(productShape.isRequired).isRequired,
    campaign: campaignShape,
  }).isRequired,
  latestTask: taskShape,
  openInvoiceId: number,
  isEditing: bool,
};

InvoiceModalContent.defaultProps = {
  latestTask: null,
  openInvoiceId: undefined,
  isEditing: true,
};

export default InvoiceModalContent;
